/* Fabian Predictive Search V12 - compact RTL UI, exact dropdown below input, alphabetical dropdown, 2026-07-02 */
window.__FABIAN_PREDICTIVE_SEARCH_VERSION = 'V12-compact-rtl-alphabetical-dropdown-2026-07-02';
/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.requestSeq = 0;
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }


    /**
     * Converts a simple one-word customer query into a Shopify prefix query.
     * Examples: 924 -> 924*, pilot -> pilot*, מחבר -> מחבר*.
     * Multi-word searches are kept unchanged to avoid hurting relevance.
     * @param {string} searchTerm - Search query.
     * @returns {string}
     */
    getShopifyQuery(searchTerm) {
      const query = searchTerm.trim();

      if (
        query.length < 3
        || query.includes('*')
        || /\s/.test(query)
        || /[:\\()]/.test(query)
        || !/[0-9A-Za-z\u0590-\u05FF]/.test(query)
      ) {
        return query;
      }

      return `${query}*`;
    }

    /**
     * Escapes text for safe HTML rendering.
     * @param {string} value - Unescaped text.
     * @returns {string}
     */
    escapeHtml(value) {
      return String(value || '').replace(/[&<>'"]/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      }[char]));
    }

    /**
     * Counts clickable result items in predictive search markup.
     * @param {string} resultsMarkup - Predictive search markup.
     * @returns {number}
     */
    countSearchResultItems(resultsMarkup) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = resultsMarkup || '';
      return tmpl.content.querySelectorAll('.predictive-search__item:not(.js-submit) .js-search-link, .predictive-search__item:not(.js-submit) a[href]').length;
    }

    /**
     * Returns the exact search form action for the current language/path.
     * @returns {string}
     */
    getSearchPath() {
      const form = this.querySelector('form.search') || this.querySelector('form');
      const action = form && form.getAttribute('action');
      return action || (theme.routes && (theme.routes.search || theme.routes.searchUrl || theme.routes.search_url)) || '/search';
    }

    /**
     * Builds a full search URL that matches the regular Shopify search page.
     * @param {string} searchTerm - Query to send to /search, usually with * for simple prefixes.
     * @returns {string}
     */
    getFullSearchUrl(searchTerm) {
      const params = new URLSearchParams();
      params.set('q', searchTerm);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      params.set('options[unavailable_products]', 'last');

      if (this.productTypeInput && this.productTypeInput.value !== '') {
        params.set('filter.p.product_type', this.productTypeInput.value);
      }

      return `${this.getSearchPath()}?${params.toString()}`;
    }

    /**
     * Returns the query as customers should see it in the dropdown header.
     * @param {string} searchTerm - Query used for Shopify search, possibly ending with *.
     * @returns {string}
     */
    getDisplayQuery(searchTerm = '') {
      const query = String(searchTerm || this.getQuery() || '').trim();
      return query.replace(/\*+$/, '');
    }

    /**
     * Reads the real result count from the regular search page when available.
     * @param {Document} doc - Parsed regular search page.
     * @param {Element} resultRoot - Results root from the regular search page.
     * @returns {number|null}
     */
    getSearchResultsCount(doc, resultRoot) {
      const countEl = resultRoot && resultRoot.hasAttribute && resultRoot.hasAttribute('data-search-results-count')
        ? resultRoot
        : doc.querySelector('[data-search-results-count]');

      if (!countEl) return null;

      const rawCount = countEl.getAttribute('data-search-results-count');
      const parsedCount = parseInt(rawCount, 10);
      return Number.isFinite(parsedCount) && parsedCount >= 0 ? parsedCount : null;
    }

    /**
     * Creates the compact top summary for the dropdown.
     * @param {string} searchTerm - Query to send to the full search page.
     * @param {number|null} totalCount - Total search result count, if known.
     * @returns {string}
     */
    getSearchSummaryMarkup(searchTerm, totalCount = null) {
      const displayQuery = this.getDisplayQuery(searchTerm);
      const href = this.getFullSearchUrl(searchTerm);
      const escapedQuery = this.escapeHtml(displayQuery);

      let summaryText = `תוצאות עבור: <bdi class="fabian-search-summary__query">${escapedQuery}</bdi>`;
      if (Number.isFinite(totalCount) && totalCount > 0) {
        summaryText = totalCount === 1
          ? `נמצאה תוצאה אחת עבור: <bdi class="fabian-search-summary__query">${escapedQuery}</bdi>`
          : `נמצאו ${totalCount} תוצאות עבור: <bdi class="fabian-search-summary__query">${escapedQuery}</bdi>`;
      }

      return `<div class="fabian-search-summary" dir="rtl">
        <div class="fabian-search-summary__text">${summaryText}</div>
        <a class="fabian-search-summary__link js-search-link" href="${this.escapeHtml(href)}">הצג הכל</a>
      </div>`;
    }

    /**
     * Adds a compact top summary to markup returned by Shopify predictive search.
     * Regular-search fallback markup already includes the summary with the real count.
     */
    ensureSearchSummary() {
      if (!this.results || this.results.querySelector('.fabian-search-summary')) return;
      if (!this.results.querySelector('a[href*="/products/"]')) return;

      const panel = this.results.querySelector('.predictive-search__results')
        || this.results.querySelector('.predictive-search__scroller')
        || this.results.querySelector('.predictive-search');
      if (!panel) return;

      const searchTerm = this.getShopifyQuery(this.getQuery());
      panel.insertAdjacentHTML('afterbegin', this.getSearchSummaryMarkup(searchTerm, null));
    }

    /**
     * Normalizes a price amount to a compact RTL-safe display format.
     * @param {string} value - Raw price value, usually containing the shekel symbol.
     * @returns {string}
     */
    normalizePriceAmount(value) {
      let price = String(value || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      price = price
        .replace(/^₪\s*/, '')
        .replace(/\s*₪$/, '')
        .replace(/\s+/g, ' ')
        .trim();

      return price ? `${price} ₪` : '';
    }

    /**
     * Converts a display price into a comparable number.
     * @param {string} value - Display price such as 41.90 ₪.
     * @returns {number}
     */
    parsePriceNumber(value) {
      const raw = String(value || '')
        .replace(/[^0-9.,]/g, '')
        .replace(/,/g, '')
        .trim();

      const parsed = parseFloat(raw);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    /**
     * Cleans price-related text without removing the actual amounts.
     * @param {string} value - Raw text from a price element/card.
     * @returns {string}
     */
    cleanPriceSourceText(value) {
      return String(value || '')
        .replace(/\u00a0/g, ' ')
        .replace(/מחיר\s+ליחידה/g, ' ')
        .replace(/ליחידה/g, ' ')
        .replace(/Regular\s+price/gi, ' ')
        .replace(/Sale\s+price/gi, ' ')
        .replace(/Unit\s+price/gi, ' ')
        .replace(/מחיר\s*החל\s*מ\s*[-–—־]?\s*/g, 'החל מ־')
        .replace(/החל\s*מ\s*[-–—־]?\s*/g, 'החל מ־')
        .replace(/מחיר\s+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    /**
     * Extracts all shekel amounts from text, preserving order and removing duplicates.
     * @param {string} value - Raw text.
     * @returns {string[]}
     */
    extractPriceAmounts(value) {
      const text = this.cleanPriceSourceText(value);
      const matches = text.match(/(?:₪\s*)?\d[\d\s,]*(?:[.,]\d{1,2})?\s*₪|₪\s*\d[\d\s,]*(?:[.,]\d{1,2})?/g) || [];
      const seen = new Set();
      const prices = [];

      matches.forEach((match) => {
        const normalized = this.normalizePriceAmount(match);
        if (!normalized || seen.has(normalized)) return;
        seen.add(normalized);
        prices.push(normalized);
      });

      return prices;
    }

    /**
     * Extracts the first or last price from an element/text.
     * @param {Element|string|null} source - Price element or raw text.
     * @param {string} position - first or last.
     * @returns {string}
     */
    getPriceAmount(source, position = 'last') {
      if (!source) return '';
      const text = typeof source === 'string' ? source : source.textContent;
      const prices = this.extractPriceAmounts(text);
      if (!prices.length) return '';
      return position === 'first' ? prices[0] : prices[prices.length - 1];
    }

    /**
     * Builds safe HTML for a regular or from-price display.
     * Rules:
     * - Variant/range products: החל מ־ + price.
     * - Regular single-price products: מחיר ליח׳ + price.
     * @param {string} price - Price text.
     * @param {boolean} hasFromPrice - Whether the product has a "from" price.
     * @returns {{html: string, text: string}}
     */
    makeRegularPriceResult(price, hasFromPrice = false) {
      if (!price) return { html: '', text: '' };

      if (hasFromPrice) {
        const display = `החל מ־${price}`;
        return {
          html: `<span class="fabian-price-from">החל מ־<bdi class="fabian-price-amount" dir="ltr">${this.escapeHtml(price)}</bdi></span>`,
          text: display
        };
      }

      const display = `מחיר ליח׳ ${price}`;
      return {
        html: `<span class="fabian-price-label">מחיר ליח׳</span><bdi class="fabian-price-current fabian-price-amount" dir="ltr">${this.escapeHtml(price)}</bdi>`,
        text: display
      };
    }

    /**
     * Builds safe HTML for sale + regular price display.
     * Rules:
     * - Sale products: מחיר ליח׳ + sale price + original price with strikethrough.
     * - If the extracted prices are reversed, use the lower price as the sale price.
     * @param {string} salePrice - Current/sale price.
     * @param {string} regularPrice - Original/compare-at price.
     * @returns {{html: string, text: string}}
     */
    makeSalePriceResult(salePrice, regularPrice) {
      if (!salePrice) return { html: '', text: '' };
      if (!regularPrice || regularPrice === salePrice) return this.makeRegularPriceResult(salePrice);

      const saleNumber = this.parsePriceNumber(salePrice);
      const regularNumber = this.parsePriceNumber(regularPrice);

      if (saleNumber && regularNumber && saleNumber > regularNumber) {
        const temp = salePrice;
        salePrice = regularPrice;
        regularPrice = temp;
      }

      return {
        html: `<span class="fabian-price-label">מחיר ליח׳</span><bdi class="fabian-price-sale fabian-price-amount" dir="ltr">${this.escapeHtml(salePrice)}</bdi><bdi class="fabian-price-regular fabian-price-amount" dir="ltr">${this.escapeHtml(regularPrice)}</bdi>`,
        text: `מחיר ליח׳ ${salePrice} ${regularPrice}`
      };
    }

    /**
     * Checks whether the product card appears to have a sale/compare-at price.
     * @param {Element} card - Product card element.
     * @returns {boolean}
     */
    cardLooksOnSale(card) {
      if (!card) return false;
      const classText = (card.className || '').toString();
      const text = card.textContent || '';
      return /price--on-sale|price__sale|price-item--sale|compare|was|מבצע|מחיר\s+רגיל|מחיר\s+מבצע/i.test(classText)
        || /מחיר\s+רגיל|מחיר\s+מבצע|Regular\s+price|Sale\s+price|Compare\s+at/i.test(text)
        || !!card.querySelector('.price--on-sale, .price__sale, .price-item--sale, .price__was, .price__compare, .compare-at-price, .was-price, s, del, [class*="compare"], [class*="was"]');
    }

    /**
     * Extracts a clean display price from a product card.
     * When a sale/compare-at price exists, returns both prices so the discount remains visible.
     * Removes unit-price text and fixes "החל מ־" spacing.
     * @param {Element} card - Product card element from the regular search page.
     * @returns {{html: string, text: string}}
     */
    extractPrice(card) {
      if (!card || !card.querySelectorAll) return { html: '', text: '' };

      const priceRoot = card.querySelector('.card__price, .product-card__price, .price') || card;
      const hasFromPrice = /מחיר\s*החל\s*מ|החל\s*מ|from/i.test(priceRoot.textContent || '');
      const onSale = this.cardLooksOnSale(priceRoot) || this.cardLooksOnSale(card);

      if (onSale) {
        const saleSelectors = [
          '.price__sale .price__current',
          '.price--on-sale .price__current',
          '.price-item--sale',
          '[class*="sale-price"]',
          '[class*="sale"] .price-item',
          '[class*="sale"]',
          '.price__current'
        ].join(',');

        const regularSelectors = [
          '.price__was',
          '.price__compare',
          '.compare-at-price',
          '.price-item--regular',
          '.price__regular .price-item',
          '.was-price',
          's',
          'del',
          '[class*="compare"]',
          '[class*="was"]'
        ].join(',');

        const saleEl = Array.from(priceRoot.querySelectorAll(saleSelectors)).find((el) => this.getPriceAmount(el));
        const regularEl = Array.from(priceRoot.querySelectorAll(regularSelectors)).find((el) => this.getPriceAmount(el));

        let salePrice = this.getPriceAmount(saleEl, 'last');
        let regularPrice = this.getPriceAmount(regularEl, 'first');

        const allPrices = this.extractPriceAmounts(priceRoot.textContent || card.textContent);
        if ((!salePrice || !regularPrice || salePrice === regularPrice) && allPrices.length >= 2) {
          regularPrice = allPrices[0];
          salePrice = allPrices[allPrices.length - 1];
        }

        const saleResult = this.makeSalePriceResult(salePrice, regularPrice);
        if (saleResult.html) return saleResult;
      }

      const preferredSelectors = [
        '.price__current',
        '.price-item--sale',
        '.price__regular .price-item--regular',
        '.price-item--regular',
        '.price__default',
        '.card__price',
        '.price',
        '[class*="price"]'
      ].join(',');

      const candidates = Array.from(priceRoot.querySelectorAll(preferredSelectors));
      for (const el of candidates) {
        const clone = el.cloneNode(true);
        clone.querySelectorAll('.visually-hidden, .sr-only, .unit-price, .price__unit, [class*="unit-price"]').forEach((node) => node.remove());

        const amount = this.getPriceAmount(clone, 'last');
        if (amount) return this.makeRegularPriceResult(amount, hasFromPrice);
      }

      const fallbackPrices = this.extractPriceAmounts(priceRoot.textContent || card.textContent);
      return this.makeRegularPriceResult(fallbackPrices[fallbackPrices.length - 1] || '', hasFromPrice);
    }

    /**
     * Normalizes product titles for stable alphabetical sorting in the dropdown.
     * @param {string} title - Product title.
     * @returns {string}
     */
    normalizeTitleForSort(title) {
      return String(title || '')
        .replace(/[\u200e\u200f]/g, '')
        .replace(/^[\s\-–—:|]+/, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    /**
     * Sorts product result objects alphabetically by title, with Hebrew and numbers handled naturally.
     * This affects only the dropdown display; the full search page remains unchanged.
     * @param {Array<{title: string}>} items - Product result items.
     * @returns {Array<{title: string}>}
     */
    sortSearchItemsByTitle(items) {
      const list = Array.isArray(items) ? items.slice() : [];

      try {
        const collator = new Intl.Collator(['he', 'en'], {
          sensitivity: 'base',
          numeric: true,
          ignorePunctuation: true
        });

        return list.sort((a, b) => collator.compare(
          this.normalizeTitleForSort(a && a.title),
          this.normalizeTitleForSort(b && b.title)
        ));
      } catch (error) {
        return list.sort((a, b) => this.normalizeTitleForSort(a && a.title).localeCompare(this.normalizeTitleForSort(b && b.title)));
      }
    }

    /**
     * Sorts any already-rendered dropdown lists alphabetically by visible product title.
     * @param {ParentNode} root - Results root.
     */
    sortRenderedResultLists(root) {
      if (!root || !root.querySelectorAll) return;

      root.querySelectorAll('.predictive-search__list').forEach((list) => {
        const items = Array.from(list.children).filter((item) => {
          if (!item.matches || !item.matches('.predictive-search__item')) return false;
          if (item.classList.contains('js-submit') || item.classList.contains('fabian-view-all-item')) return false;
          return !!item.querySelector('a[href*="/products/"]');
        });

        if (items.length < 2) return;

        try {
          const collator = new Intl.Collator(['he', 'en'], {
            sensitivity: 'base',
            numeric: true,
            ignorePunctuation: true
          });

          items.sort((a, b) => collator.compare(
            this.normalizeTitleForSort((a.querySelector('.predictive-result__title') || a).textContent),
            this.normalizeTitleForSort((b.querySelector('.predictive-result__title') || b).textContent)
          ));
        } catch (error) {
          items.sort((a, b) => this.normalizeTitleForSort((a.querySelector('.predictive-result__title') || a).textContent)
            .localeCompare(this.normalizeTitleForSort((b.querySelector('.predictive-result__title') || b).textContent)));
        }

        items.forEach((item) => list.appendChild(item));
      });
    }

    /**
     * Safely extracts a product URL pathname from a link.
     * @param {HTMLAnchorElement} link - Product link.
     * @returns {string}
     */
    getProductKey(link) {
      try {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        return url.pathname.replace(/\/$/, '');
      } catch (e) {
        return link.getAttribute('href').split('?')[0].split('#')[0];
      }
    }

    /**
     * Creates dropdown markup from the regular search page.
     * This is intentionally used first for prefix searches, because the regular
     * Shopify search page returns the full result set while the predictive endpoint
     * can return only one result.
     * @param {string} shopifySearchTerm - The query sent to Shopify, usually with *.
     * @returns {Promise<string>}
     */
    async getRegularSearchDropdownResults(shopifySearchTerm) {
      const response = await fetch(this.getFullSearchUrl(shopifySearchTerm), {
        credentials: 'same-origin'
      });
      if (!response.ok) return '';

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const resultRoot = doc.querySelector('#filter-results') || doc.querySelector('.main-products-grid__results') || doc;
      const totalCount = this.getSearchResultsCount(doc, resultRoot);
      const cards = Array.from(resultRoot.querySelectorAll('.js-pagination-result, .card, .product-card, article, li'));
      const seenProducts = new Set();
      const themeLimit = parseInt(theme.settings.pSearchLimit, 10) || 0;
      const limit = Math.max(themeLimit, 8);
      const items = [];

      for (const card of cards) {
        const productLink = Array.from(card.querySelectorAll('a[href*="/products/"]')).find((link) => link.getAttribute('href'));
        if (!productLink) continue;

        const productKey = this.getProductKey(productLink);
        if (seenProducts.has(productKey)) continue;

        const titleSelectors = [
          '.card__title a',
          '.card__title',
          '.product-card__title a',
          '.product-card__title',
          '.card__heading a',
          '.card__heading',
          'h3 a',
          'h2 a',
          'h3',
          'h2'
        ].join(',');
        const titleEl = card.querySelector(titleSelectors);
        let title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';

        if (!title) {
          const visibleProductLink = Array.from(card.querySelectorAll('a[href*="/products/"]'))
            .map((link) => link.textContent.trim().replace(/\s+/g, ' '))
            .filter((text) => text.length > 1)
            .sort((a, b) => b.length - a.length)[0];
          title = visibleProductLink || productLink.getAttribute('aria-label') || productLink.getAttribute('title') || '';
        }

        if (!title) continue;

        const image = card.querySelector('img');
        const imageSrc = image && (
          image.currentSrc
          || image.src
          || image.getAttribute('data-src')
          || image.getAttribute('src')
        );
        const imageAlt = image && (image.getAttribute('alt') || title);
        const price = this.extractPrice(card);
        const href = productLink.getAttribute('href');

        seenProducts.add(productKey);
        items.push({ href, title, imageSrc, imageAlt, price });
      }

      // If the card-based extraction did not work, fall back to grouping every product link.
      if (!items.length) {
        const productLinks = Array.from(resultRoot.querySelectorAll('a[href*="/products/"]'));
        for (const link of productLinks) {
          const productKey = this.getProductKey(link);
          if (seenProducts.has(productKey)) continue;

          const title = link.textContent.trim().replace(/\s+/g, ' ')
            || link.getAttribute('aria-label')
            || link.getAttribute('title')
            || '';
          if (!title) continue;

          const card = link.closest('.js-pagination-result, .card, .product-card, article, li') || link.parentElement;
          const image = card && card.querySelector('img');
          const imageSrc = image && (image.currentSrc || image.src || image.getAttribute('data-src') || image.getAttribute('src'));
          const imageAlt = image && (image.getAttribute('alt') || title);

          seenProducts.add(productKey);
          items.push({ href: link.getAttribute('href'), title, imageSrc, imageAlt, price: null });
        }
      }

      if (!items.length) return '';

      const sortedItems = this.sortSearchItemsByTitle(items).slice(0, limit);
      const fallbackId = `${this.input.id}-fallback-products`;
      const listItems = sortedItems.map((item, index) => {
        const mediaMarkup = item.imageSrc
          ? `<div class="predictive-result__media media relative"><img src="${this.escapeHtml(item.imageSrc)}" loading="eager" alt="${this.escapeHtml(item.imageAlt)}"></div>`
          : '<div class="predictive-result__media media relative"></div>';

        const priceMarkup = item.price && item.price.html
          ? `<div class="predictive-result__sub-title text-sm">${item.price.html}</div>`
          : '';

        return `<li class="predictive-search__item" id="${fallbackId}-item-${index}" role="option">
          <a class="predictive-result flex items-start focus-inset js-search-link" href="${this.escapeHtml(item.href)}">
            ${mediaMarkup}
            <div class="predictive-result__info flex-auto">
              <h3 class="predictive-result__title h6">${this.escapeHtml(item.title)}</h3>
              ${priceMarkup}
            </div>
          </a>
        </li>`;
      }).join('');

      const safeTotalCount = Number.isFinite(totalCount) && totalCount >= sortedItems.length ? totalCount : null;
      const summaryMarkup = this.getSearchSummaryMarkup(shopifySearchTerm, safeTotalCount);

      return `<div class="predictive-search absolute bg-theme-bg text-theme-text text-start fabian-regular-search-results" data-fabian-search-source="regular-search">
        <div class="predictive-search__scroller overflow-auto">
          <div class="predictive-search__results flex flex-col">
            ${summaryMarkup}
            <div class="predictive-search__tabpanel focus-inset" id="${fallbackId}" role="tabpanel" tabindex="0">
              <ul class="predictive-search__list" role="list">
                ${listItems}
              </ul>
            </div>
          </div>
        </div>
      </div>`;
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const query = this.getQuery();

      if (
        !query.length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
        return;
      }

      const shopifyQuery = this.getShopifyQuery(query);
      if (shopifyQuery !== query) {
        this.input.value = shopifyQuery;
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const requestId = ++this.requestSeq;
      this.setLiveRegionLoadingState();

      const shopifySearchTerm = this.getShopifyQuery(searchTerm);

      let searchFields = 'title,product_type,variants.title,vendor,variants.sku,variants.barcode';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(shopifySearchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(shopifySearchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[type]')}=product`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        if (requestId === this.requestSeq) this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        // For prefix searches such as 924*, PIL*, and Hebrew prefixes, the regular
        // Shopify search page returns the full result set while the predictive endpoint
        // may return only one product. Therefore, use the regular search page first.
        if (shopifySearchTerm !== searchTerm) {
          const fallbackMarkup = await this.getRegularSearchDropdownResults(shopifySearchTerm);
          if (fallbackMarkup && this.countSearchResultItems(fallbackMarkup) > 0) {
            this.cachedResults[queryKey] = fallbackMarkup;
            if (requestId === this.requestSeq) this.renderResults(fallbackMarkup);
            return;
          }
        }

        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (requestId !== this.requestSeq) return;

        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Removes duplicated view-all/count blocks and old parallel dropdowns.
     * This keeps one clean results panel even if previous theme markup is still present.
     * @param {string} resultsMarkup - Raw markup.
     * @returns {string}
     */
    cleanResultsMarkup(resultsMarkup) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = resultsMarkup || '';

      // Remove the old Shopify predictive "view all (1)" block when it is mixed
      // with the regular-search fallback list.
      tmpl.content.querySelectorAll('.predictive-search__view-all, .predictive-search__item.js-submit, button, a').forEach((el) => {
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        const looksLikeOldCountButton = /לכל\s+תוצאות\s+החיפוש|View all|כל\s+התוצאות/i.test(text) && /\(\d+\)/.test(text);
        const isSubmitItem = el.classList && (el.classList.contains('js-submit') || el.classList.contains('predictive-search__view-all'));

        if (looksLikeOldCountButton || isSubmitItem) {
          const item = el.closest('.predictive-search__item, li, .predictive-search__view-all') || el;
          item.remove();
        }
      });

      this.removeNoResultsMessagesWhenProductsExist(tmpl.content);
      tmpl.content.querySelectorAll('.tablist').forEach((el) => el.remove());

      // If two predictive panels somehow exist in the markup, keep the one with
      // actual product links, preferring the Fabian regular-search fallback.
      const panels = Array.from(tmpl.content.querySelectorAll('.predictive-search'));
      if (panels.length > 1) {
        const fallbackPanel = panels.find((panel) => panel.classList.contains('fabian-regular-search-results'));
        const productPanel = panels.find((panel) => panel.querySelector('a[href*="/products/"]'));
        const keep = fallbackPanel || productPanel || panels[panels.length - 1];
        tmpl.innerHTML = keep.outerHTML;
      }

      return tmpl.innerHTML;
    }


    /**
     * Removes stale "no results" text when a later/fallback request did find products.
     * This mainly protects against out-of-order predictive-search responses while typing.
     * @param {ParentNode} root - Root node to clean.
     */
    removeNoResultsMessagesWhenProductsExist(root) {
      if (!root || !root.querySelector || !root.querySelector('a[href*="/products/"]')) return;

      const noResultsPattern = /לא\s+נמצאו\s+תוצאות|No\s+results|No\s+results\s+found|לא\s+נמצאו/i;

      root.querySelectorAll('.predictive-search__empty, .predictive-search__no-results, .search__empty, .no-results, [class*="no-result"], [class*="empty"]').forEach((el) => {
        if (!el.querySelector('a[href*="/products/"]')) el.remove();
      });

      // Remove text-only/leaf elements that say "no results" without touching product cards.
      Array.from(root.querySelectorAll('*')).forEach((el) => {
        if (el.querySelector('a[href*="/products/"]')) return;

        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text || text.length > 80) return;

        if (noResultsPattern.test(text)) {
          const removable = el.closest('.predictive-search__item, li, p, div') || el;
          if (!removable.querySelector('a[href*="/products/"]')) removable.remove();
        }
      });
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = '';
      this.results.insertAdjacentHTML('afterbegin', this.cleanResultsMarkup(resultsMarkup));
      this.removeNoResultsMessagesWhenProductsExist(this.results);

      // Safety cleanup after the browser has parsed the markup.
      const panels = Array.from(this.results.querySelectorAll('.predictive-search'));
      if (panels.length > 1) {
        const keep = panels.find((panel) => panel.classList.contains('fabian-regular-search-results'))
          || panels.find((panel) => panel.querySelector('a[href*="/products/"]'))
          || panels[panels.length - 1];
        this.results.innerHTML = keep.outerHTML;
      }

      this.results.querySelectorAll('.predictive-search__view-all, .predictive-search__item.js-submit').forEach((el) => {
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (/לכל\s+תוצאות\s+החיפוש|View all|כל\s+התוצאות/i.test(text) || /\(\d+\)/.test(text)) {
          (el.closest('.predictive-search__item, li') || el).remove();
        }
      });
      this.results.querySelectorAll('.tablist').forEach((el) => el.remove());

      this.removeNoResultsMessagesWhenProductsExist(this.results);
      this.sortRenderedResultLists(this.results);
      this.ensureSearchSummary();

      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
